import {
  Component,
  OnDestroy,
  OnInit,
  HostBinding,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { Subscription } from 'rxjs';
import { LayoutService } from '@app/shared/services/layout.service';
import { AuthService } from '@app/api/api-services/auth.service';
import { RouteHelperService } from '@app/shared/services/route-helper.service';
import { NavNotificationsService } from '@app/api/api-services/nav-notifications.service';
import { UserInfo } from '../../../shared/models';
import { NotificationItemApiModel, NotificationsCounterApiModel } from '@app/api/api-models';
import { RedirectService } from 'src/app/shared/services/redirect.service';
import { TokenStorageService } from '@app/shared/services';
import { EidAuthService } from '@app/auth/services/eid-auth.service';
import { ConfigService } from '@app/configuration/config.service';
import { TopNavBarService } from '@shared/services/top-nav.service';
import { NotificationCounterService } from '@app/shared/services/notification-counter.service';

@Component({
  selector: 'a360-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NavbarComponent implements AfterViewInit, OnDestroy, OnInit, OnChanges {
  @ViewChild('appswitcher') appSwitchComponent: ElementRef;

  public counters: NotificationsCounterApiModel;
  public isNotificationsOpen = false;
  public pending = false;
  public userInfo: UserInfo;
  public notifications: NotificationItemApiModel[] = [];
  public eidToken: string = null;

  public get logoSrc(): string {
    return this.userInfo.CurrentSite?.LogoSrc ?? 'assets/img/PowerReady-logo-white.png';
  }

  public get shouldShowAppSwitcher(): boolean {
    return !this.userInfo.IsSysAdmin && this.userInfo.CurrentSite?.PowerSuitEidAuthEnabled && this.eidToken !== null;
  }

  private subscriptions: Array<Subscription>;
  private timers: number[] = [];
  private resizeHandler: () => void;

  constructor(
    public readonly routeHelper: RouteHelperService,
    public readonly auth: AuthService,
    public readonly eidAuth: EidAuthService,
    private readonly tokenStorage: TokenStorageService,
    private readonly notificationsService: NavNotificationsService,
    private readonly layoutService: LayoutService,
    private readonly redirectService: RedirectService, // DO NOT REMOVE!!!
    private readonly topnavBarService: TopNavBarService,
    private readonly notificationCounterService: NotificationCounterService,
    private readonly configService: ConfigService
  ) {
    this.subscriptions = new Array<Subscription>();
    this.redirectService.shadowInit();
  }

  public ngOnInit(): void {
    this.notificationCounterService.startRefreshingNotifications();
    this.subscriptions.push(
      this.notificationCounterService.counters$.subscribe((counters) => {
        this.counters = counters;
      })
    );
    this.subscriptions.push(
      this.notificationsService.subscribeNotificationsStateObserver().subscribe((isNotificationsOpen: boolean) => {
        this.isNotificationsOpen = isNotificationsOpen;
      })
    );
    this.subscriptions.push(
      this.notificationsService.subscribeNotificationStateActionObserver().subscribe((model) => {
        this.notificationCounterService.handleNotificationStateAction(model);
      })
    );

    if (!this.tokenStorage.userInfo) {
      return;
    }
    this.userInfo = this.tokenStorage.userInfo;
    this.eidAuth.accessToken.subscribe((token) => {
      this.eidAuth.setToken(token);
    });

    this.eidAuth.accessToken$.subscribe((token) => {
      this.eidToken = token;
      if (this.shouldShowAppSwitcher) {
        this.createAppSwitcher();
      }
    });

    this.subscribeToUserInfoChanges();

    this.resizeHandler = () => this.handleWindowResize();
    window.addEventListener('resize', this.resizeHandler);

    const timer = window.setTimeout(() => {
      this.handleWindowResize();
    }, 1000);
    this.timers.push(timer);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.eidToken = changes.eidToken.currentValue;
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
    this.notificationCounterService.stopRefreshingNotifications();
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers = [];
  }

  public ngAfterViewInit() {
    if (this.shouldShowAppSwitcher) {
      this.createAppSwitcher();
    }
  }

  @HostBinding('class.disabled')
  public get disabled(): boolean {
    return this.layoutService.barsDisabled;
  }

  public toggleSidebar(): void {
    this.layoutService.toggleSidebar();
    this.notificationsService.closeNotifications();
  }

  private handleWindowResize() {
    this.topnavBarService.setSidebarOpenCloseState();
  }

  public toggleNotifications(): void {
    this.isNotificationsOpen = !this.isNotificationsOpen;
    this.notificationsService.emitNotificationsStateObserver(this.isNotificationsOpen);

    this.topnavBarService.setSidebarOpenCloseState();
  }

  private subscribeToUserInfoChanges(): void {
    const subscription = this.tokenStorage.userInfoChanged.subscribe((userInfo: UserInfo) => {
      this.userInfo = userInfo;
    });

    this.subscriptions.push(subscription);
  }

  public logout(): void {
    this.topnavBarService.logout();
  }

  public myProfile(): void {
    this.topnavBarService.myProfile(this.userInfo.UserId);
  }

  private createAppSwitcher() {
    const appSwitcherConfig = {
      'aut-id': 'neo-app-switch',
      'auth-token': this.eidToken,
      'current-product': 'PFTO',
      'enable-learn-more': true,
      environment: this.configService.getConfig().appSwitcher.environment
    };

    const el = document.createElement('neogov-app-switch');
    Object.keys(appSwitcherConfig).forEach((key) => {
      el.setAttribute(key, appSwitcherConfig[key]);
    });

    const container = document.getElementById('neogov-app-switch-container');
    container.appendChild(el);
  }
}
